import { RawUserInfo, UserInfo } from '@harvey-frontend/models/user-info'
import { DraftingApplyEditRequest } from '@harvey-frontend/openapi/models/DraftingApplyEditRequest'
import { DraftingApplyEditResponse } from '@harvey-frontend/openapi/models/DraftingApplyEditResponse'
import { DraftingEditSuggestion } from '@harvey-frontend/openapi/models/DraftingEditSuggestion'
import { DraftingEditSuggestionsRequest } from '@harvey-frontend/openapi/models/DraftingEditSuggestionsRequest'
import { DraftingEditSuggestionsStreamingResponse } from '@harvey-frontend/openapi/models/DraftingEditSuggestionsStreamingResponse'
import { DraftingInsertRequest } from '@harvey-frontend/openapi/models/DraftingInsertRequest'
import { DraftingSummarizeAndCiteRequest } from '@harvey-frontend/openapi/models/DraftingSummarizeAndCiteRequest'
import { DraftingSummarizeAndCiteResponse } from '@harvey-frontend/openapi/models/DraftingSummarizeAndCiteResponse'
import { FormatOoxmlRequest } from '@harvey-frontend/openapi/models/FormatOoxmlRequest'
import { FormatOoxmlResponse } from '@harvey-frontend/openapi/models/FormatOoxmlResponse'
import {
  ToFrontendKeys,
  ToBackendKeys,
  CamelToSnake,
} from '@harvey-frontend/utils/utils'
import * as Sentry from '@sentry/react'

import Services from 'services'

import { ReviewResponse } from '../schemas/playbook'
import { fetchBackendStream } from './backend-fetch'

const ENDPOINTS = {
  USER_INFO: 'user_info',
  DRAFTING_SUGGEST_STREAM: 'word_add_in/suggest_stream',
  DRAFTING_APPLY: 'word_add_in/apply_edit',
  DIRECT_EDITS: 'word_add_in/edits_v2',
  DRAFTING_INSERT_STREAM: 'word_add_in/insert',
  DRAFTING_SUMMARIZE_AND_CITE: 'word_add_in/summarize_and_cite',
  RUN_PLAYBOOK_REVIEW: 'playbook/review_document',
  FORMAT_OOXML: 'word_add_in/format_ooxml',
}

// Intersection type of all streaming responses. This allows us to type-check
// the response keys in the streaming response.
export type StreamingContainerResponse =
  DraftingEditSuggestionsStreamingResponse & DraftingApplyEditResponse
export type StreamingContainerKeys =
  | CamelToSnake<keyof StreamingContainerResponse>
  | 'object'
// Type of the response key in the streaming response.
export type StreamingResponseType =
  | StreamingContainerResponse[keyof StreamingContainerResponse]
  | ReviewResponse
  | DraftingApplyEditResponse
type StreamingRequestType =
  | DraftingEditSuggestionsRequest
  | DraftingInsertRequest

interface FetchAndConvertStreamRequest<T extends StreamingResponseType> {
  endpoint: string
  token: string
  abortController: AbortController
  requestParams: StreamingRequestType | FormData
  // Key in the response object that should be extracted into the response array.
  responseKey: StreamingContainerKeys
  // If provided, the callback will be debounced with the given wait time.
  debounceWait?: number
  updateCallback?: (response: T) => Promise<void>
}

async function fetchAndConvertRest<
  T extends FormData | object,
  U extends string | object,
>(endpoint: string, request: T): Promise<U> {
  const backendRequest =
    request instanceof FormData ? request : ToBackendKeys(request)
  const response = await Services.Backend.Post<U>(endpoint, backendRequest)
  const parsedData = safeParseJson(response)
  return ToFrontendKeys(parsedData)
}

const safeParseJson = (data: string | object): object | string => {
  if (typeof data === 'object') return data
  try {
    return JSON.parse(data)
  } catch (e) {
    Sentry.captureException(e)
    throw e
  }
}

export async function fetchAndConvertStream<T extends StreamingResponseType>(
  request: FetchAndConvertStreamRequest<T>
): Promise<T[]> {
  const body =
    request.requestParams instanceof FormData
      ? request.requestParams
      : ToBackendKeys(request.requestParams)

  const response = await fetchBackendStream<T>({
    route: request.endpoint,
    token: request.token,
    abortController: request.abortController,
    body,
    responseKey: request.responseKey,
    debounceWait: request.debounceWait,
    updateCallback: request.updateCallback,
  })
  return ToFrontendKeys(response)
}

/**
 * Fetches user info from the backend. If the user is not authenticated, the backend
 * will return a 401 error, which will be propagated to the caller.
 */
export const fetchUserInfo = async (): Promise<UserInfo | null> => {
  const rawUserInfo = await Services.Backend.Get<RawUserInfo>(
    ENDPOINTS.USER_INFO,
    { throwOnError: true }
  )
  return new UserInfo(rawUserInfo)
}

interface FetchStreamingRequest<T extends StreamingResponseType> {
  accessToken: string
  abortController: AbortController
  request: DraftingEditSuggestionsRequest | DraftingInsertRequest
  updateCallback?: (response: T) => Promise<void>
}

export const fetchDraftingSuggestStream = async ({
  accessToken,
  abortController,
  request,
  updateCallback,
}: FetchStreamingRequest<DraftingEditSuggestion>): Promise<
  DraftingEditSuggestion[]
> => {
  return fetchAndConvertStream<DraftingEditSuggestion>({
    endpoint: ENDPOINTS.DRAFTING_SUGGEST_STREAM,
    token: accessToken,
    abortController,
    requestParams: request,
    responseKey: 'suggestion',
    updateCallback,
  })
}

export const fetchEdits = async (
  request: DraftingEditSuggestionsRequest
): Promise<DraftingApplyEditResponse> => {
  const response = await Services.Backend.Post<DraftingApplyEditResponse>(
    ENDPOINTS.DIRECT_EDITS,
    request
  )
  return ToFrontendKeys(response)
}

export const fetchDraftingInsertStream = async ({
  accessToken,
  abortController,
  request,
  updateCallback,
}: FetchStreamingRequest<string>): Promise<string[]> => {
  return fetchAndConvertStream<string>({
    endpoint: ENDPOINTS.DRAFTING_INSERT_STREAM,
    token: accessToken,
    abortController,
    requestParams: request,
    responseKey: 'edited_text',
    // The latency of applying individual words is pretty high. Debouncing lets us
    // batch groups of streamed insertions together.
    debounceWait: 25,
    updateCallback,
  })
}

export const fetchFormatOoxml = async (request: FormatOoxmlRequest) => {
  return fetchAndConvertRest<FormatOoxmlRequest, FormatOoxmlResponse>(
    ENDPOINTS.FORMAT_OOXML,
    request
  )
}

interface PlaybookReviewStreamRequest<T extends StreamingResponseType> {
  accessToken: string
  abortController: AbortController
  formData: FormData
  updateCallback?: (response: T) => Promise<void>
}

export const runPlaybookReview = async <T extends StreamingResponseType>({
  accessToken,
  abortController,
  formData,
  updateCallback,
}: PlaybookReviewStreamRequest<T>): Promise<T[]> => {
  return fetchAndConvertStream<T>({
    endpoint: ENDPOINTS.RUN_PLAYBOOK_REVIEW,
    token: accessToken,
    abortController,
    requestParams: formData,
    responseKey: 'object',
    updateCallback,
  })
}

export const fetchDraftingApply = async (
  request: DraftingApplyEditRequest
): Promise<DraftingApplyEditResponse> => {
  return fetchAndConvertRest<
    DraftingApplyEditRequest,
    DraftingApplyEditResponse
  >(ENDPOINTS.DRAFTING_APPLY, request)
}

export const fetchSummarizeAndCite = async (
  request: DraftingSummarizeAndCiteRequest
): Promise<DraftingSummarizeAndCiteResponse> => {
  return fetchAndConvertRest<
    DraftingSummarizeAndCiteRequest,
    DraftingSummarizeAndCiteResponse
  >(ENDPOINTS.DRAFTING_SUMMARIZE_AND_CITE, request)
}
